import type { Filter } from '@repo/api-codegen';
import {
	apiListTeamsV2QueryKey,
	fetchApiListTeamsV2,
} from '@repo/api-codegen/v2';
import { FilterOptionType } from '@repo/common/components/Filter';
import type {
	FilterDropdownConfigList,
	FilterItem,
	FilterOption,
} from '@repo/common/components/Filter/types';
import {
	FilterDropdownType,
	FilterOperator,
} from '@repo/common/components/Filter/types';
import { IconWrapper } from '@repo/common/components/IconWrapper';
import { Icon } from '@repo/foundations';

import type { UserOut as IUser, TeamOut } from '@repo/api-codegen/v2';
import { fetchUserList } from '../../api';
import { apiClient, getEndpoints } from '../../api/common';
import queryClient from '../../api/queryClient';
import { FILTER_OPTIONS_CONFIG } from '../../components/Filter/constants';
import { UserAvatar } from '../../components/UserAvatar';
import { TIME_SAVINGS_CATEGORIES } from '../../features/AiImpactDashboard/constants';

const SYSTEM_RATING_FILTER_ITEMS = [
	{ label: 'Good', value: 'good' },
	{ label: 'Moderate', value: 'moderate' },
	{ label: 'Poor', value: 'poor' },
] as const;

const TIME_SAVINGS_CATEGORY_FILTER_ITEMS = TIME_SAVINGS_CATEGORIES.map(
	(category) => ({
		label: category.label,
		value: category.key,
	})
);

const buildUserFilterItem = (user: IUser): FilterItem => ({
	label: user.display_name || 'Untitled',
	value: user.id,
	metadata: 'user',
	icon: <UserAvatar key={user.id} user={user} size="xs" />,
});

const getUsersFilterItemsById: NonNullable<
	FilterDropdownConfigList['getItemsById']
> = async (ids: string[]) => {
	const uniqueIds = Array.from(new Set(ids.filter(Boolean)));

	if (uniqueIds.length === 0) {
		return [];
	}

	let usersResults: IUser[] = [];

	try {
		const { results = [] } = await fetchUserList({
			page: 1,
			page_size: uniqueIds.length,
			filters: {
				disabled: false,
				pending: false,
				id__in: uniqueIds.join(','),
			},
		});

		usersResults = results ?? [];
	} catch {
		usersResults = [];
	}

	const itemsById = new Map<string, FilterItem>(
		usersResults.map((user) => [user.id, buildUserFilterItem(user)])
	);

	const missingIds = uniqueIds.filter((id) => !itemsById.has(id));

	if (missingIds.length > 0) {
		const additionalItems = await Promise.all(
			missingIds.map(async (id) => {
				try {
					const { data } = await apiClient.get<IUser>(
						getEndpoints(['user']).byId(id)
					);
					return buildUserFilterItem(data);
				} catch {
					return null;
				}
			})
		);

		additionalItems
			.filter((item): item is FilterItem => Boolean(item))
			.forEach((item) => {
				itemsById.set(item.value as string, item);
			});
	}

	return uniqueIds
		.map((id) => itemsById.get(id))
		.filter((item): item is FilterItem => Boolean(item));
};

export function getAiChatsFilterOptions(): FilterOption[] {
	const createdTimeOption =
		FILTER_OPTIONS_CONFIG[FilterOptionType.CREATED_TIME];
	const frequentUsersOption =
		FILTER_OPTIONS_CONFIG[FilterOptionType.FREQUENT_USERS];
	const personaOption = FILTER_OPTIONS_CONFIG[FilterOptionType.AI_PERSONA];
	const frequentUsersDropdownConfig =
		frequentUsersOption.filterDropdownConfig as FilterDropdownConfigList;

	const usersFilter: FilterOption = {
		...frequentUsersOption,
		label: 'Users',
		filterDropdownConfig: {
			...frequentUsersDropdownConfig,
			getItems: async (page: number = 1, searchTerm: string = '') => {
				const { results: usersResults } = await fetchUserList({
					filters: {
						disabled: false,
						pending: false,
					},
				});

				return (
					usersResults
						?.filter(
							(user: IUser) =>
								!searchTerm ||
								user.display_name
									?.toLowerCase()
									?.includes(searchTerm.toLowerCase()) ||
								user.email?.toLowerCase()?.includes(searchTerm.toLowerCase())
						)
						.map((user: IUser) => ({
							label: user.display_name || 'Untitled',
							value: user.id,
							metadata: 'user',
							icon: <UserAvatar key={user.id} user={user} size="xs" />,
						})) ?? []
				);
			},
			getItemsById: getUsersFilterItemsById,
		},
	};

	const createdFilter: FilterOption = {
		...createdTimeOption,
		label: 'Date',
		filterDropdownConfig: {
			...createdTimeOption.filterDropdownConfig,
		},
	};

	const sentimentFilter: FilterOption = {
		label: 'Feedback',
		type: 'sentiment' as any,
		field: 'sentiment',
		filterDropdownConfig: {
			searchPlaceholder: 'Search feedback',
			multipleCountsSuffix: 'feedback',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async () => [
				{
					label: 'Positive',
					value: 'positive',
					icon: <Icon name="thumbUp" size="md" color="icon/primary/default" />,
				},
				{
					label: 'Negative',
					value: 'negative',
					icon: (
						<Icon name="thumbDown" size="md" color="icon/primary/default" />
					),
				},
			],
		},
	};

	const personaFilter: FilterOption = {
		...personaOption,
	};

	const categoryFilter: FilterOption = {
		label: 'Category',
		type: 'time_savings_categories' as FilterOptionType,
		field: 'calculated_time_savings_categories__contains',
		filterDropdownConfig: {
			searchPlaceholder: 'Search categories',
			multipleCountsSuffix: 'categories',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: (_page, searchTerm = '') => {
				const normalizedSearch = searchTerm.toLowerCase();
				return TIME_SAVINGS_CATEGORY_FILTER_ITEMS.filter((item) =>
					item.label.toLowerCase().includes(normalizedSearch)
				);
			},
			getItemsById: (ids: string[]) => {
				const idSet = new Set(ids);
				return TIME_SAVINGS_CATEGORY_FILTER_ITEMS.filter((item) =>
					idSet.has(item.value)
				);
			},
			convertToCatalogFilter: async (value) => {
				if (!value) {
					return null;
				}

				const categoryValue = String(value);

				return {
					field: 'calculated_time_savings_categories__contains',
					operator: 'exact',
					value: [categoryValue],
					operands: [],
				} as Filter;
			},
		},
	};

	const teamFilter: FilterOption = {
		label: 'Team',
		type: FilterOptionType.TEAMS,
		field: 'teams',
		filterDropdownConfig: {
			searchPlaceholder: 'Search teams',
			multipleCountsSuffix: 'teams',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (_page?: number, searchTerm?: string) => {
				const teams = await queryClient.fetchQuery({
					queryKey: apiListTeamsV2QueryKey({}),
					queryFn: ({ signal }) => fetchApiListTeamsV2({}, signal),
				});
				return (
					teams
						?.filter(
							(team: TeamOut) =>
								!searchTerm ||
								team.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
						)
						.map((team: TeamOut) => ({
							label: team.name,
							value: team.id,
							icon: <IconWrapper key={team.id}>{team.icon}</IconWrapper>,
						})) ?? []
				);
			},
			getItemsById: async (ids: string[]) => {
				const teams = await queryClient.fetchQuery({
					queryKey: apiListTeamsV2QueryKey({}),
					queryFn: ({ signal }) => fetchApiListTeamsV2({}, signal),
				});
				const idSet = new Set(ids.filter(Boolean));
				return (
					teams
						?.filter((team: TeamOut) => idSet.has(team.id))
						.map((team: TeamOut) => ({
							label: team.name,
							value: team.id,
							icon: <IconWrapper key={team.id}>{team.icon}</IconWrapper>,
						})) ?? []
				);
			},
			convertToCatalogFilter: async (value) => {
				const values = Array.isArray(value) ? value : [value];
				const filteredValues = values.filter(
					(v): v is string => v !== null && v !== undefined
				);
				if (filteredValues.length === 0) {
					return null;
				}
				return {
					field: 'teams',
					operator: 'in',
					value: filteredValues,
					operands: [],
				} as Filter;
			},
		},
	};

	const systemRatingFilter: FilterOption = {
		label: 'System Rating',
		type: 'system_rating' as FilterOptionType,
		field: 'calculated_system_rating',
		filterDropdownConfig: {
			searchPlaceholder: 'Search ratings',
			multipleCountsSuffix: 'ratings',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: (_page, searchTerm = '') => {
				const normalizedSearch = searchTerm.toLowerCase();
				return SYSTEM_RATING_FILTER_ITEMS.filter((item) =>
					item.label.toLowerCase().includes(normalizedSearch)
				);
			},
			getItemsById: (ids: string[]) => {
				const idSet = new Set(ids);
				return SYSTEM_RATING_FILTER_ITEMS.filter((item) =>
					idSet.has(item.value)
				);
			},
		},
	};

	return [
		usersFilter,
		createdFilter,
		personaFilter,
		categoryFilter,
		teamFilter,
		systemRatingFilter,
		sentimentFilter,
	];
}
